// src/hooks/useAnalytics.js
import { useMemo } from 'react';
import { calculateLeadEfficiency } from '../utils/dashboardUtils';
import {
  processCallsToAnalytics,
  applyCumulativeView,
  aggregateTrendsByGranularity,
  combineTrendsWithPrevious
} from '../utils/analyticsUtils';
import { getGranularityForPeriod, getTrendDateRange, getPreviousTrendDateRange } from '../utils/dateUtils';

export const useAnalytics = (
  calls,
  previousCalls,
  customerTargets,
  viewOptions = {},
  useAdminLeadLogic = false,
  priceMap = null,
  period = null,
  trendCalls = null,  // Extended date range calls for trends
  previousTrendCalls = null  // Previous period for trend comparison
) => {
  const analytics = useMemo(() => {
    if (!calls || !Array.isArray(calls)) return getEmptyAnalytics();

    // Process current period (for KPIs and tables)
    const currentAnalytics = processCallsToAnalytics(calls, customerTargets, useAdminLeadLogic, priceMap);

    // Process previous period (for KPI comparisons)
    const previousAnalytics = processCallsToAnalytics(previousCalls || [], customerTargets, useAdminLeadLogic, priceMap);

    // For trends: use extended date range if provided, otherwise fall back to current period
    const trendSourceCalls = trendCalls && trendCalls.length > 0 ? trendCalls : calls;
    const trendSourceAnalytics = trendCalls && trendCalls.length > 0
      ? processCallsToAnalytics(trendSourceCalls, customerTargets, useAdminLeadLogic, priceMap)
      : currentAnalytics;

    // Process previous trend period for comparison (if available)
    const previousTrendAnalytics = previousTrendCalls && previousTrendCalls.length > 0
      ? processCallsToAnalytics(previousTrendCalls, customerTargets, useAdminLeadLogic, priceMap)
      : null;

    // Determine granularity based on period
    const granularity = getGranularityForPeriod(period);

    // Range meegeven zodat buckets zonder calls als 0-punt verschijnen.
    // Alleen wanneer we ook echt de extended trend-range gefetcht hebben;
    // bij de fallback (KPI-periode als trendbron) zou vullen misleiden.
    const usingTrendCalls = !!(trendCalls && trendCalls.length > 0);
    const trendRange = usingTrendCalls && period ? getTrendDateRange(period) : null;
    const previousTrendRange = previousTrendAnalytics && period
      ? getPreviousTrendDateRange(period)
      : null;

    // Aggregate current trends by granularity (day/week/month)
    const aggregatedCurrentTrends = aggregateTrendsByGranularity(
      trendSourceAnalytics.trends || [],
      granularity,
      trendRange
    );

    // Aggregate previous trends by granularity (for comparison)
    const aggregatedPreviousTrends = previousTrendAnalytics
      ? aggregateTrendsByGranularity(previousTrendAnalytics.trends || [], granularity, previousTrendRange)
      : null;

    // Combine current and previous trends (align by index for comparison)
    const combinedTrends = combineTrendsWithPrevious(
      aggregatedCurrentTrends,
      aggregatedPreviousTrends
    );

    // Apply cumulative view if requested
    const finalTrends = viewOptions.cumulativeView
      ? applyCumulativeView(combinedTrends)
      : combinedTrends;

    // Calculate lead efficiency (leads / unique calls)
    const leadEfficiency = calculateLeadEfficiency(
      currentAnalytics.leadCalls,
      currentAnalytics.totalUnique
    );

    const result = {
      ...currentAnalytics,
      leadEfficiency,
      trends: finalTrends,
      granularity, // Include granularity for chart formatting
      topCustomers: currentAnalytics.customers
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 20), // Top 20 customers
      previousPeriod: {
        totalRevenue: previousAnalytics.totalRevenue,
        totalCalls: previousAnalytics.totalCalls,
        leadCalls: previousAnalytics.leadCalls,
        trends: previousAnalytics.trends,
        niches: previousAnalytics.niches
      }
    };

    return result;
  }, [calls, previousCalls, customerTargets, viewOptions.cumulativeView, useAdminLeadLogic, priceMap, period, trendCalls, previousTrendCalls]);

  return analytics;
};

const getEmptyAnalytics = () => ({
  totalRevenue: 0,
  totalCalls: 0,
  leadCalls: 0,
  nonLeadCalls: 0,
  leadEfficiency: 0,
  campaigns: [],
  niches: [],
  classifications: [],
  trends: [],
  topCustomers: [],
  previousPeriod: {
    totalRevenue: 0,
    totalCalls: 0,
    leadCalls: 0,
    trends: [],
    niches: []
  }
});
